import { Controller } from "stimulus";
import {
  debounce,
  getCSRFToken, getRecentSearchIds,
  isMobileDevice,
  setUpdatedCsrfMetaContent
} from "../../util";
import { getSmartResultHtmlByType } from "../../template/smart_results_templates";
import { getActiveCurrency } from "../../util/currency";
import {
  EventFocusPortfolioCoinSearch,
  EventPriceLoaded,
  EventRetryPortfolioAddCoin,
  EventTabItemPointerentered,
  EventPortfolioHoverCardSelectPortfolio,
  EventCheckUserLogin
} from "../../constants/events";
import { trackEvent } from "../../util/analytics";
import { SELECT_RETRY_ADD_PORTFOLIO_COIN_CTA_EVENT } from "../../constants/mixpanel_config";
import {
  getSkeletonTemplate,
  getSmartResultSkeletonTemplate,
} from "../../template/search/section_templates";

const MAX_SEARCHED_COINS_COUNT = 3;
const MAX_SELECTED_COINS_LIMIT = 50;

export default class extends Controller {
  static targets = [
    // Search bar
    "searchInput",

    // Coins
    "defaultCoinsContainer", "searchResultsContainer", "loading",
    "addCoinsRow", "addCoinsButton", "coinsCounter",

    // Highlight pills
    "defaultHighlightPills", "searchHighlightPills",

    // Short results
    "recentlySearchedCoinsContainer", "shortResultsContainer",

    // Smart results
    "smartResultContainer", "adContainer"
  ];

  // We use this variable to prevent race conditions from AJAX calls
  // completing at different times.
  _ajaxCount = 0;
  _selectedPill = null;
  _selectedCoinsFromAddCoinsModal = [];

  async connect() {
    window.addEventListener(EventCheckUserLogin, async(e) => {
      this.userLoggedIn = e.detail.userLoggedIn;

      if (this.userLoggedIn) {
        await this._fetchPortfolioData();
        this._renderSmartResultsAd();

        this.handleSearchInput = debounce(this._handleSearchInput.bind(this), 300);
        this.renderSmartResult = debounce(this.renderSmartResult, 100);

        this._focusSearchInput();

        await this._renderCoinsLists();
        this._renderFirstCoinSmartResult();

        this._handleRetry();

        if (this.hasDefaultHighlightPillsTarget) {
          this._selectedPill = this.defaultHighlightPillsTarget.querySelector("nav a.selected");
        }

        window.addEventListener(EventPortfolioHoverCardSelectPortfolio, async (event) => {
          this.portfolioId = event.detail.portfolioId;
          await this._fetchPortfolioData(this.portfolioId);
        });
      }
    });
  }

  async _fetchPortfolioData(portfolioId = null) {
    if (!portfolioId) {
      let match = window.location.pathname.match(/\/portfolios\/(\d+)/);
      portfolioId = match ? +match[1] : null;
    }

    const url = `/${I18n.locale}/portfolios/portfolio_data?id=${portfolioId}`;
    await fetch(url, { credentials: "same-origin" })
      .then(response => response.text())
      .then(result => {
        if (!result) {
          return;
        }

        const data = JSON.parse(result)
        this.portfolioId = +data.portfolio_id;
        this.portfolioCoinIds = data.portfolio_coins_ids.map(id => +id);
      });
  }

  _renderSmartResultsAd() {
    const url = `/${I18n.locale}/portfolios/add_coin_modal_smart_results_ad`;
    fetch(url, { credentials: "same-origin" })
      .then(response => response.text())
      .then(result => {
        if (!result) {
          return;
        }

        if (this.hasAdContainerTarget) {
          this.adContainerTarget.innerHTML = result;
        }
      });
  }

  closeModal() {
    if (this._selectedCoinsFromAddCoinsModal.length > 0) {
      Modal.confirm(
        I18n.t("portfolio.add_coin.warning.title"),
        I18n.t("portfolio.add_coin.warning.caption_going_back"),
        I18n.t("portfolio.add_coin.warning.cta.continue_adding_coins"),
        I18n.t("portfolio.add_coin.warning.cta.go_back_anyway"),
      ).then((result) => {
        if (result.confirm || result?.source === "click_away") return;

        // Clear selections when switching
        this._selectedCoinsFromAddCoinsModal = [];

        // User confirmed to close modal
        Modal.hide("portfolio_add_coin");
        this._updateSelectionIcon();
        this._processCoinSelectionSize();
      });
    } else {
      Modal.hide("portfolio_add_coin");
    }
  }

  handleCoinSelect(e) {
    const coinId = +e.currentTarget.dataset.coinId;

    if (this._selectedCoinsFromAddCoinsModal.includes(coinId)) {
      this._removeIdFromArray(coinId, this._selectedCoinsFromAddCoinsModal);
    } else {
      this._selectedCoinsFromAddCoinsModal.push(coinId);
    }

    this._updateSelectionIcon();
    this._processCoinSelectionSize();
  }

  updateActivePill(e) {
    const pillIdOfMouseoveredElement = e.currentTarget.dataset.tabId;

    if (pillIdOfMouseoveredElement === this._selectedPill.id) {
      return;
    }

    this._selectedPill = this.defaultHighlightPillsTarget.querySelector(`#${pillIdOfMouseoveredElement}`);
    this._selectedPill.dispatchEvent(new CustomEvent(EventTabItemPointerentered));
    this._selectedPill.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  renderSmartResult(e) {
    if (isMobileDevice(navigator.userAgent)) {
      return;
    }

    if (this.hasSmartResultContainerTarget) {
      this.smartResultContainerTarget.innerHTML = getSmartResultHtmlByType(e);
    }

    this.element.dispatchEvent(
      new CustomEvent(EventPriceLoaded, {
        bubbles: true
      })
    );
  }

  scrollToDivider(e) {
    let dividerId = e.currentTarget.dataset.divider;
    let scrollToElement = document.getElementById(dividerId);

    scrollToElement?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });

    if (this._selectedPill === e.currentTarget) {
      return;
    }

    this._selectedPill = e.currentTarget;
  }

  _handleSearchInput() {
    const query = this.searchInputTarget.value;

    this._ajaxCount += 1;
    const ajaxCount = this._ajaxCount;

    if (!query) {
      this.defaultCoinsContainerTarget.classList.remove("tw-hidden");
      this.searchResultsContainerTarget.classList.add("tw-hidden");
      this.defaultHighlightPillsTarget.classList.remove("tw-hidden");
      this.loadingTarget.classList.add("!tw-hidden");

      return;
    }

    this.loadingTarget.classList.remove("!tw-hidden");

    const params = new URLSearchParams();
    params.set("query", query);
    params.set("vs_currency", getActiveCurrency());

    const url = `/${I18n.locale}/portfolios/${this.portfolioId}/search?${params}`;
    fetch(url, { credentials: "same-origin" })
      .then(response => response.text())
      .then(result => {
        if (!result || this._ajaxCount !== ajaxCount) {
          return;
        }

        this.defaultHighlightPillsTarget.classList.add("tw-hidden");
        this.defaultCoinsContainerTarget.classList.add("tw-hidden");

        this.searchResultsContainerTarget.innerHTML = result;
        this.searchResultsContainerTarget.classList.remove("tw-hidden");

        this._updateSelectionIcon();
        this._processCoinSelectionSize();
        this._removeAddedCoinsAction();

        this.loadingTarget.classList.add("!tw-hidden");
      });
  }

  _renderFirstCoinSmartResult(){
    let element = document.querySelector("#portfolio-trending-divider");
    this.renderSmartResult(element.nextElementSibling);
  }

  async _renderCoinsLists() {
    if (this.hasShortResultsContainerTarget) {
      this.shortResultsContainerTarget.innerHTML = getSkeletonTemplate();
    }

    if (this.hasSmartResultContainerTarget) {
      this.smartResultContainerTarget.innerHTML = getSmartResultSkeletonTemplate();
    }

    await this._loadRecentlySearched();

    const url = `/${I18n.locale}/portfolios/${this.portfolioId}/coins_for_add_coins_modal`;
    await fetch(url, { credentials: "same-origin" })
      .then(response => response.text())
      .then(result => {
        if (!result) {
          return;
        }

        if (this.hasShortResultsContainerTarget) {
          this.shortResultsContainerTarget.innerHTML = result;
        }
      });
  }

  async _loadRecentlySearched() {
    const recentlySearchedChip = document.getElementById("tab-recently-searched")
    const recentSearchIds = getRecentSearchIds(MAX_SEARCHED_COINS_COUNT);

    if (recentSearchIds.length === 0) {
      let trendingDivider = document.getElementById("portfolio-trending-divider");
      trendingDivider?.classList?.remove("tw-my-2");
      trendingDivider?.classList?.add("tw-mb-2");

      return;
    }

    await this._fetchRecentlySearched(recentSearchIds);
    recentlySearchedChip.classList.remove("tw-hidden");
    recentlySearchedChip.click();
  }

  async _fetchRecentlySearched(coinIds) {
    const params = new URLSearchParams();
    params.set("coin_ids", coinIds);
    params.set("vs_currency", getActiveCurrency());

    const url = `/${I18n.locale}/portfolios/${this.portfolioId}/recently_searched?${params}`;
    await fetch(url, { credentials: "same-origin" })
      .then(response => response.text())
      .then(result => {
        if (!result) {
          return;
        }

        if (this.hasRecentlySearchedCoinsContainerTarget) {
          this.recentlySearchedCoinsContainerTarget.innerHTML += result;
        }
      });
  }

  _handleRetry() {
    window.addEventListener(EventRetryPortfolioAddCoin, () => {
      trackEvent(SELECT_RETRY_ADD_PORTFOLIO_COIN_CTA_EVENT, { "origin": "portfolio" });
      this.bulkAddCoinsToPortfolio();
    });
  }

  _updateSelectionIcon() {
    this.element.querySelectorAll(`i.selection-icon`).forEach(circle => {
      const coinId = +circle.getAttribute("data-coin-id");
      if (!coinId) return;

      if (this._selectedCoinsFromAddCoinsModal.includes(coinId)) {
        circle.classList.remove("far", "fa-circle", "tw-text-gray-400");
        circle.classList.add("fas", "fa-circle-check", "tw-text-primary-500", "dark:tw-text-primary-400");
      } else {
        circle.classList.remove("fas", "fa-circle-check", "tw-text-primary-500", "dark:tw-text-primary-400");
        circle.classList.add("far", "fa-circle", "tw-text-gray-400");
      }
    });
  }

  _processCoinSelectionSize() {
    if (this._selectedCoinsFromAddCoinsModal.length >= MAX_SELECTED_COINS_LIMIT) {
      this._updateCoinsCounter(true);
      this._updateButtonsAvailability(true);
    } else {
      this._updateCoinsCounter(false);
      this._updateButtonsAvailability(false);
    }
  }

  _updateCoinsCounter(atMaxLimit = false) {
    if (!this.hasCoinsCounterTarget) return;

    if (atMaxLimit) {
      this.coinsCounterTarget.innerHTML = I18n.t("user_onboarding.coins_selected_max_limit", {
        count: this._selectedCoinsFromAddCoinsModal.length
      });
    } else {
      this.coinsCounterTarget.innerHTML = I18n.t("user_onboarding.coins_selected", {
        count: this._selectedCoinsFromAddCoinsModal.length
      });
    }
  }

  _updateButtonsAvailability(atMaxLimit) {
    this.addCoinsButtonTarget.disabled = this._selectedCoinsFromAddCoinsModal.length <= 0;

    if (!atMaxLimit) {
      this.addCoinsRowTargets.forEach(button => { button.disabled = false; });
      return;
    }

    this.addCoinsRowTargets.forEach(button => {
      const coinId = +button.dataset.coinId;
      if (!coinId) return;

      if (!this._selectedCoinsFromAddCoinsModal.includes(coinId)) {
        button.disabled = true;
      }
    });
  }

  async bulkAddCoinsToPortfolio() {
    this.loadingTarget.classList.remove("!tw-hidden");

    const params = new URLSearchParams();
    params.set("coin_ids", this._selectedCoinsFromAddCoinsModal.join(','));

    await setUpdatedCsrfMetaContent();
    const url = `/en/portfolios/${this.portfolioId}/coins/bulk_add_coins`;

    fetch(url, {
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": getCSRFToken(),
      },
      body: JSON.stringify({
        coin_ids: this._selectedCoinsFromAddCoinsModal
      })
    }).then((response) => {
      if (!response.ok) {
        Toaster.toast(I18n.t("portfolio.add_coin.error.title"), {
          action: {
            message: I18n.t("portfolio.add_coin.error.retry"),
            options: {
              onclick: "window.dispatchEvent(new CustomEvent('coingecko-portfolio-bulk-add-coin-retry'));",
            }
          },
          icon: {
            classes: "fas fa-times tw-text-danger-500"
          }
        });
      } else {
        window.location.href = `/${I18n.locale}/portfolios/${this.portfolioId}`;
      }
    });
  }

  _removeAddedCoinsAction() {
    this.addCoinsRowTargets.forEach(button => {
      const added = button.dataset.hasAdded === "true";
      if (!added) return;

      button.removeAttribute("data-action");
      button.classList.remove("tw-cursor-pointer");
      button.classList.add('!tw-cursor-default');
    });
  }

  _removeIdFromArray(coinId, array) {
    let coinIdIndex = array.indexOf(coinId);
    
    if (coinIdIndex !== -1) {
      array.splice(coinIdIndex, 1);
    }
  }

  _focusSearchInput() {
    window.addEventListener(EventFocusPortfolioCoinSearch, (e) => {
      e.preventDefault();
      setTimeout( () => { this.searchInputTarget.focus() }, 10 );
    });
  }
}
